import React, { useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';

import { useNavigate } from 'react-router-dom';
import { ITriggerData } from '../CategorizeContainer';
import Spinner, { SpinnerColor } from '../../../generic-components/Spinner';
import { ICategory, ITransaction } from '../../../types';
import {
  ANIMATION_CLASS_MAP,
  ANIMATION_DURATION_MS,
  CARD_ICON_CDN_BASE_URL,
  CATEGORIZATION_TYPES,
  MAX_TRANSACTION_INDEX,
  STYLES,
} from '../constants';
import { CATEGORIZE_EXPENSES } from '../../dashboard/constants';

import Heading from '../../../generic-components/Heading';
import defaultExpenseIcon from '../../../assets/icons/svg/colored-icons/ic-colored-expense-transparent.svg';
import defaultIncomeIcon from '../../../assets/icons/svg/colored-icons/ic-colored-category-default.svg';
import { refactoredTimeAndDate } from '../../../helpers/timeFormatter.helper';
import { DATE_FORMAT, FEATURE_FLAG } from '../../../constants/general';
import { convertValueToUSD } from '../../../helpers/currency.helper';
import { LiliToolTip } from '../../../generic-components/LiliToolTip';
import { RollingNumbers } from '../../../generic-components/RollingNumbers';
import { MasterOfCategorization } from './MasterOfCategorization';
import Icon from '../../../generic-components/Icon';
import LiliAiIcon from '../../../assets/icons/svg/lili-ai.svg';
import { validateFeatureFlag } from '../../../services/featureFlagService';
import { ChooseCategory } from './ChooseCategory';
import Button from '../../../generic-components/Button';
import { VIEWS_MAP } from '../../../router/constants';

interface IProps {
  transactionsList: ITransaction[] | null;
  isLoading: boolean;
  triggerAction: ITriggerData;
  handleActionFinish: (triggerType: string) => void;
  onCategorySelection: () => void;
  transactionsCategories: ICategory[];
  categorizationType: string;
  onFirstCardChange?: (cardId: string) => void;
  finishProcessCtaTitle?: string;
}

export const CategorizeMainSection = ({
  transactionsList,
  isLoading,
  triggerAction,
  handleActionFinish,
  onCategorySelection,
  transactionsCategories,
  categorizationType,
  onFirstCardChange,
  finishProcessCtaTitle,
}: IProps) => {
  const elFirstCard = useRef<HTMLDivElement | null>(null);
  const elSecondCard = useRef<HTMLDivElement | null>(null);
  const { t } = useTranslation();
  const navigate = useNavigate();
  const getCardIcon = (catId: string | undefined) => {
    const category = transactionsCategories?.filter((c) => c.id === catId)[0];
    if (category && category.imageName) {
      return `${CARD_ICON_CDN_BASE_URL}${category.imageName.replaceAll(' ', '')}.svg`;
    }
    if (categorizationType === CATEGORIZATION_TYPES.INCOME) {
      return defaultIncomeIcon;
    }
    return defaultExpenseIcon;
  };

  const getRotationByIdx = (idx: number) => {
    if (!idx) return '';
    if (idx % 2 === 0) {
      return STYLES.MAIN_SECTION.CARD_ROTATE_LEFT;
    }
    return STYLES.MAIN_SECTION.CARD_ROTATE_RIGHT;
  };

  const shouldShowPoweredByAi = useMemo(() => {
    if (isLoading || !transactionsList?.length) return false;
    return validateFeatureFlag(FEATURE_FLAG.CATEGORIZE_AI);
  }, [isLoading, transactionsList]);
  useEffect(() => {
    if (!triggerAction.isActive) return;
    if (triggerAction.isActive) {
      const animationClass = ANIMATION_CLASS_MAP[triggerAction.type as keyof typeof ANIMATION_CLASS_MAP];
      if (elFirstCard.current) {
        elFirstCard.current?.classList.add(animationClass);
      }

      if (elSecondCard.current) {
        elSecondCard.current?.classList.add('animate-resetCard');
      }

      setTimeout(() => {
        handleActionFinish(triggerAction.type);
      }, ANIMATION_DURATION_MS);
    }
  }, [triggerAction]);

  useEffect(() => {
    if (!onFirstCardChange || !transactionsList || !transactionsList.length) return;
    onFirstCardChange(transactionsList[0].transactionId);
  }, [transactionsList]);

  return (
    <div className={STYLES.MAIN_SECTION.WRAPPER}>
      {!isLoading &&
        transactionsList &&
        transactionsList.map((transaction: ITransaction, idx: number) => {
          if (idx > MAX_TRANSACTION_INDEX) return null;
          return (
            <div
              ref={!idx ? elFirstCard : idx === 1 ? elSecondCard : null}
              key={transaction.transactionId}
              style={{ zIndex: 10 - idx }}
              className={`${STYLES.MAIN_SECTION.CARD_WRAPPER} ${getRotationByIdx(idx)}`}
            >
              <div className={`${CATEGORIZE_EXPENSES.CLASSES.CARD.CONTAINER}`}>
                <ChooseCategory
                  transactionsCategories={transactionsCategories}
                  transaction={transaction}
                  onCategorySelection={onCategorySelection}
                />
                <div className={STYLES.MAIN_SECTION.CARD_ICON_WRAPPER}>
                  <img
                    alt='Icon'
                    className={STYLES.MAIN_SECTION.CARD_ICON}
                    src={getCardIcon(transaction?.categoryDetails?.categoryExternalId)}
                  />
                </div>

                {(transaction?.merchantName || transaction?.details) && (
                  <LiliToolTip
                    toolTipChildren={
                      <Heading
                        title={transaction?.merchantName || transaction?.details || ''}
                        className={STYLES.MAIN_SECTION.CARD_CONTENT_TT}
                      />
                    }
                    conditionalAppearance
                  >
                    <Heading
                      title={transaction?.merchantName || transaction?.details || ''}
                      className={STYLES.MAIN_SECTION.CARD_CONTENT}
                    />
                  </LiliToolTip>
                )}

                <RollingNumbers
                  contentStyle={`${transaction?.categorizationType === CATEGORIZATION_TYPES.INCOME ? 'text-badgeGreen03' : 'text-bw07'} ${STYLES.ROLLING_NUMBER}`}
                  value={convertValueToUSD(Math.abs(transaction?.amount), 2)}
                />

                <Heading
                  title={refactoredTimeAndDate(transaction?.timestamp, 'dashboard', DATE_FORMAT.NUMBERS, false)}
                  className={STYLES.MAIN_SECTION.CARD_DATE}
                />
              </div>
            </div>
          );
        })}

      {isLoading && <Spinner spinnerColor={SpinnerColor.Blue} height='2rem' width='2rem' />}

      {!isLoading && !transactionsList?.length && (
        <div className='flex flex-col gap-[40px]'>
          <MasterOfCategorization />
          {finishProcessCtaTitle && (
            <Button
              onClick={() => navigate(`/${VIEWS_MAP.EXPENSES}`)}
              className='mx-auto max-h-[54px] w-[200px] px-[40px] py-[17px] font-metricR text-[16px] text-bw00 mt-[30px] rounded-[4px] bg-action03 hover:bg-action04'
              title={finishProcessCtaTitle}
              id='finishProcessCta'
            />
          )}
        </div>
      )}

      {shouldShowPoweredByAi && (
        <div className='flex items-center gap-[4px] absolute bottom-0 left-[50%] translate-x-[-50%] translate-y-[15px]'>
          <Heading title={t('POWERED_BY')} className='font-metricR text-[12px] text-bw06' />
          <Icon className='w-[32px]' src={LiliAiIcon} />
        </div>
      )}
    </div>
  );
};
